/**
 * useSubmitFeedback — POSTs user feedback to nexus.
 *
 * Wraps the /ai/feedback nexus route in a thin hook that translates the
 * client-side {vote: 'up' | 'down'} to the wire score (1.0 / 0.0).
 * Returns the parsed response so callers can show success/failure UI.
 *
 * Errors do NOT throw — they resolve to {submitted: false} so the
 * modal can close cleanly without surfacing transport noise to the user.
 * Sentry already captures the underlying fetch error via apiFetch's
 * ApiError. A toast / retry UI can be added in a follow-up if needed.
 */

import { useCallback } from 'react';
import { apiFetch } from '@/lib/http/client';
import type { FeedbackVote } from '../components/messages/message-toolbar';

export interface SubmitFeedbackArgs {
  runId: string;
  vote: FeedbackVote;
  comment: string;
}

export interface SubmitFeedbackResult {
  submitted: boolean;
}

interface NexusFeedbackResponse {
  submitted: boolean;
}

export function useSubmitFeedback(): {
  submit: (args: SubmitFeedbackArgs) => Promise<SubmitFeedbackResult>;
} {
  const submit = useCallback(
    async (args: SubmitFeedbackArgs): Promise<SubmitFeedbackResult> => {
      if (!args.runId) {
        // No runId on the message (e.g., turn before data-run-id was
        // wired, or a synthetic local message). Silently noop so the
        // modal can close cleanly.
        return { submitted: false };
      }
      const score = args.vote === 'up' ? 1.0 : 0.0;
      try {
        const resp = await apiFetch<NexusFeedbackResponse>('/ai/feedback', {
          method: 'POST',
          body: JSON.stringify({
            run_id: args.runId,
            score,
            comment: args.comment,
          }),
        });
        return { submitted: Boolean(resp?.submitted) };
      } catch {
        return { submitted: false };
      }
    },
    [],
  );

  return { submit };
}
